/**
 * Onboarding Page
 * Main container for onboarding flow with step navigation
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { WelcomeStep } from './steps/WelcomeStep';
import { DashboardPreviewStep } from './steps/DashboardPreviewStep';
import { QuranReaderPreviewStep } from './steps/QuranReaderPreviewStep';
import { GoalSettingPreviewStep } from './steps/GoalSettingPreviewStep';
import { PWAInstallStep } from './steps/PWAInstallStep';

const ONBOARDING_COMPLETED_KEY = 'quran-assistant-onboarding-completed';
const TOTAL_STEPS = 5;

const steps = [
  WelcomeStep,
  DashboardPreviewStep,
  QuranReaderPreviewStep,
  GoalSettingPreviewStep,
  PWAInstallStep,
];

export function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      completeOnboarding();
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
    navigate('/login');
  };

  const CurrentStepComponent = steps[currentStep];
  const isLastStep = currentStep === TOTAL_STEPS - 1;

  return (
    <div className="h-dvh bg-background flex flex-col overflow-hidden">
      {/* Skip Button */}
      <div className="w-full flex justify-end p-3 shrink-0">
        <Button
          variant="ghost"
          onClick={handleSkip}
          className="text-muted-foreground hover:text-foreground text-sm"
        >
          ข้าม
        </Button>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto pb-36">
        <div
          className={cn(
            'w-full max-w-2xl mx-auto transition-all duration-300 ease-in-out',
            'animate-in fade-in slide-in-from-bottom-4'
          )}
          key={currentStep}
        >
          <CurrentStepComponent />
        </div>
      </div>

      {/* Fixed Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-lg border-t border-border/50 z-50"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <div className="p-4 space-y-3 max-w-2xl mx-auto">
          {/* Progress Dots */}
          <div className="flex justify-center gap-2">
            {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  index === currentStep
                    ? 'w-8 bg-primary'
                    : 'w-2 bg-muted hover:bg-muted-foreground/50'
                )}
                aria-label={`ไปยังขั้นตอนที่ ${index + 1}`}
              />
            ))}
          </div>

          {/* Next/Get Started Button */}
          <Button
            onClick={handleNext}
            className="w-full h-12 text-base font-semibold"
            size="lg"
          >
            {isLastStep ? 'เริ่มใช้งาน' : 'ถัดไป'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingPage;
